import { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc';

export interface SortConfig<T> {
  key: keyof T | string;
  direction: SortDirection;
}

export interface SortingResult<T> {
  sortedData: T[];
  sortConfig: SortConfig<T> | null;
  requestSort: (key: keyof T | string) => void;
  getSortDirection: (key: keyof T | string) => SortDirection | null;
  clearSort: () => void;
}

// Type-safe value extraction function
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

// Per-column sort-value resolver: takes a row + the active sort key and
// returns whatever value the sort should compare against. Used by columns
// that need a custom ordering (e.g. a priority enum on a status column
// that shouldn't sort alphabetically by its raw string value). When the
// resolver returns undefined OR is not provided, falls back to the default
// nested-path extraction.
export type SortValueResolver<T> = (row: T, key: string) => unknown;

export const useSorting = <T>(
  data: T[],
  defaultSortConfig?: SortConfig<T>,
  valueResolver?: SortValueResolver<T>
): SortingResult<T> => {
  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(
    defaultSortConfig ?? null
  );

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    const resolve = (row: T) => {
      const key = sortConfig.key as string;
      const custom = valueResolver?.(row, key);
      return custom !== undefined ? custom : getNestedValue(row, key);
    };

    return [...data].sort((a, b) => {
      const aValue = resolve(a);
      const bValue = resolve(b);

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle different types
      let comparison = 0;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue, undefined, { numeric: true });
      } else {
        // Convert to string for comparison
        comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
      }

      return sortConfig.direction === 'desc' ? -comparison : comparison;
    });
  }, [data, sortConfig, valueResolver]);

  const requestSort = (key: keyof T | string) => {
    let direction: SortDirection = 'desc'; // Default to descending for financial data

    if (sortConfig && sortConfig.key === key) {
      // If clicking the same column, toggle direction
      direction = sortConfig.direction === 'desc' ? 'asc' : 'desc';
    }

    setSortConfig({ key, direction });
  };

  const getSortDirection = (key: keyof T | string): SortDirection | null => {
    if (!sortConfig || sortConfig.key !== key) {
      return null;
    }
    return sortConfig.direction;
  };

  const clearSort = () => {
    setSortConfig(null);
  };

  return {
    sortedData,
    sortConfig,
    requestSort,
    getSortDirection,
    clearSort,
  };
};
